import React, { useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

// Icons
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';

interface NotificationType {
  id: number;
  message: string;
  time: string;
  read: boolean;
}

const notifications: NotificationType[] = [
  {
    id: 1,
    message: 'Your booking #12345 has been confirmed',
    time: '10 minutes ago',
    read: false,
  },
  {
    id: 2,
    message: 'New invoice available for January',
    time: '1 hour ago',
    read: false,
  },
  {
    id: 3,
    message: 'Your feedback is requested for your last trip',
    time: '3 hours ago',
    read: true,
  },
  {
    id: 4,
    message: 'Team manager has approved your request',
    time: '1 day ago',
    read: true,
  },
];

const DashboardHeader: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleProfileMenuClose();
  };

  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.read).length;

  return (
    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography 
        variant="h6" 
        component="div" 
        sx={{ 
          display: { xs: 'none', sm: 'block' },
          color: theme.palette.primary.main,
          fontWeight: 600,
        }}
      >
        Dashboard
      </Typography>

      {/* Search */}
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
        <Paper
          component="form"
          sx={{
            p: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: '70%', md: '50%' },
            borderRadius: '8px',
            boxShadow: 'none',
            border: `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <IconButton type="submit" sx={{ p: '10px' }} aria-label="search">
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Search bookings, vehicles, etc."
            inputProps={{ 'aria-label': 'search dashboard' }}
          />
        </Paper>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Notifications */}
        <IconButton 
          size="large" 
          aria-label="show notifications" 
          color="inherit"
          onClick={handleNotificationMenuOpen}
          sx={{ mr: 1 }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon sx={{ color: theme.palette.primary.main }} />
          </Badge>
        </IconButton>

        {/* User Menu */}
        <IconButton
          size="large"
          edge="end"
          aria-label="account of current user"
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          color="inherit"
        >
          <Avatar 
            alt="User" 
            src="/static/images/avatar/1.jpg"
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: theme.palette.primary.main,
            }}
          >
            JD
          </Avatar>
        </IconButton>
      </Box>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        id="notifications-menu"
        keepMounted
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            width: 320,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: '8px',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
        </Box>
        <Divider />
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id}
              onClick={handleNotificationMenuClose}
              sx={{
                py: 1.5,
                px: 2,
                borderLeft: notification.read ? 'none' : `4px solid ${theme.palette.secondary.main}`,
                bgcolor: notification.read ? 'transparent' : 'rgba(212, 175, 55, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: notification.read ? 400 : 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {notification.time}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}
        <Divider />
        <MenuItem onClick={handleNotificationMenuClose} sx={{ justifyContent: 'center' }}>
          <Typography 
            variant="body2" 
            color="primary" 
            sx={{ fontWeight: 500 }}
          >
            View All Notifications
          </Typography>
        </MenuItem>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        id="profile-menu"
        keepMounted
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            width: 220,
            borderRadius: '8px',
            mt: 1.5,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            John Doe
          </Typography>
          <Typography variant="body2" color="text.secondary">
            john.doe@company.com
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => handleNavigation('/dashboard/profile')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help & Support</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleNavigation('/login')} sx={{ color: '#dc3545' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#dc3545' }} />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardHeader; 